import React, { Fragment, useMemo, useCallback, useState, useRef } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import cx from 'classnames';

import { Icon } from 'components';

import {
  convertTimeLabelToValue,
  convertTimeValueToLabel,
  isTimeInputValid,
  isTimeInputValidTillNow,
  CAMPAIGN_TIME_OPTIONS,
} from 'utils/time';
import styles from './TimePicker.module.scss';

export const MINUTES_15 = 60 * 15 * 1000;

type TimePickerProps = {
  value?: string;
  onChange: (value: string) => void;
  hasError?: boolean;
  helperText?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  triggerIcon?: JSX.Element;
  customTime?: string;
};

export const TimePicker: React.FC<TimePickerProps> = ({
  label,
  value,
  onChange,
  hasError,
  disabled,
  helperText,
  className,
  placeholder,
  triggerIcon,
  customTime,
}) => {
  const selected = useMemo(() => {
    if (!value) return;

    const selected = CAMPAIGN_TIME_OPTIONS.find(
      (option) => option.value === value
    );
    if (selected) {
      return selected;
    } else {
      const label = convertTimeValueToLabel(value);

      return { label, value };
    }
  }, [value]);

  const [timeInput, setTimeInput] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const isCustomTimeValid =
    timeInput === null ? true : isTimeInputValidTillNow(timeInput);

  const classnames = cx(
    styles.dropdown,
    {
      [styles['has-error']]: hasError || !isCustomTimeValid,
      [styles['disabled-dropdown']]: disabled,
    },
    className
  );

  const onTimeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTimeInput(event.target.value);
    },
    []
  );

  const onComboboxValueChange = (value: string) => {
    onChange(value);
    setTimeInput(null);
  };

  const displayValue = (open: boolean) => {
    const value = open
      ? typeof timeInput === 'string'
        ? timeInput
        : selected?.label || ''
      : selected?.label || '';
    return value;
  };

  const onDropdownClose = () => {
    if (timeInput !== null && !isTimeInputValid(timeInput)) {
      setTimeInput(null);

      return;
    }

    if (timeInput !== null) {
      validateTimeValue(timeInput);
    }
  };

  const onInputKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    //@ts-ignore
    const value = event.target.value;

    /** This is to prevent element selection on enter */
    if (event.key === 'Enter') {
      const valid = isTimeInputValid(value);
      if (!value || !valid) {
        setTimeInput(null);
        buttonRef.current?.click();
      } else {
        validateTimeValue(value);
        buttonRef.current?.click();
      }
    }
  };

  const validateTimeValue = (input: string) => {
    const value = convertTimeLabelToValue(input);
    onComboboxValueChange(value);
  };

  return (
    <div className={classnames}>
      <Combobox
        value={value}
        onChange={onComboboxValueChange}
        disabled={disabled}
      >
        {({ open }) => (
          <>
            {label ? <Combobox.Label>{label}</Combobox.Label> : null}
            <Combobox.Button
              className={cx(styles['button'], styles['custom-time-btn'])}
              ref={buttonRef}
              as="div"
            >
              <Combobox.Input
                className={cx(
                  { [styles.invalid]: !isTimeInputValid },
                  styles['custom-time-input']
                )}
                onChange={onTimeChange}
                displayValue={() => displayValue(open)}
                onKeyDown={onInputKeyUp}
                placeholder={placeholder}
              />
              {triggerIcon ? (
                triggerIcon
              ) : (
                <Icon
                  iconName={open ? 'chevron-up' : 'chevron-down'}
                  size="medium"
                  className={styles.triangle}
                />
              )}
            </Combobox.Button>
            <Transition
              enter={styles.enter}
              enterFrom={styles['enter-from']}
              enterTo={styles['enter-to']}
              leave={styles.leave}
              leaveFrom={styles['leave-from']}
              leaveTo={styles['leave-to']}
              as={Fragment}
              afterLeave={onDropdownClose}
            >
              <Combobox.Options>
                <div className={styles['dynamic-options-scroll']}>
                  {CAMPAIGN_TIME_OPTIONS.map((option) =>
                    !customTime || (customTime && option.id > customTime) ? (
                      <Combobox.Option key={option.id} value={option.value}>
                        {option.label}
                      </Combobox.Option>
                    ) : null
                  )}
                </div>
              </Combobox.Options>
            </Transition>
          </>
        )}
      </Combobox>
      {helperText && <p className={styles.helper}>{helperText}</p>}
    </div>
  );
};
